import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { useOrgWriteGate } from '@/hooks/useRoleCheck';
import { useFeatureFlags } from './useFeatureFlags';

/** Result of {@link useReleaseManagementApiKeys}. */
export interface ManagementApiKeysRelease {
  /** Render the Management API Keys card at all. */
  isVisible: boolean;
  /** Enable the Create / Revoke controls (support sees them disabled). */
  canManage: boolean;
  /** Flag or role state is still resolving; nothing should render yet. */
  isLoading: boolean;
}

/**
 * Combined flag + role gate for the Management API Keys card (KIR-3748).
 *
 * Mirrors the backend exactly: the list route is `manager | owner | support`,
 * the create/revoke routes are `manager | owner`. So support sees the list with
 * the write controls disabled, and a member sees nothing at all.
 *
 * `isVisible` is `false` while loading and on a flag-fetch error — the safe
 * default, matching `useIsSettingsRedesignEnabled`. Hiding an available feature
 * is recoverable by a refresh; showing a card whose every request 404s is not.
 */
export function useReleaseManagementApiKeys(
  organizationId: string | undefined
): ManagementApiKeysRelease {
  const {
    flags,
    isLoading: flagsLoading,
    isError,
  } = useFeatureFlags([flagsmithFeatureFlags.MANAGEMENT_API_KEYS]);
  const writeGate = useOrgWriteGate(organizationId);

  const flagEnabled =
    (flags[flagsmithFeatureFlags.MANAGEMENT_API_KEYS] ?? false) && !isError;
  const isLoading = flagsLoading || writeGate.isLoading;

  if (isLoading) {
    return { isVisible: false, canManage: false, isLoading: true };
  }

  const isVisible = flagEnabled && !writeGate.hidden;

  return {
    isVisible,
    canManage: isVisible && !writeGate.disabled,
    isLoading: false,
  };
}
